import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { PageHeaderService } from '@app/shared/modules/page-header/@core/page-header.service';
import { AuthStore } from '@core/auth/auth.store';
import { AuthURL } from '@core/config/auth-url.config';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs';
import { AppConstants } from '../entities/app-constants';
import { AlertService } from './alert.service';
import { CredentialsService } from './credentials.service';
import { HttpService } from './http.service';
import { LocalService } from './local.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { DynamicTabPageService } from '@app/pages/dynamic-tab-page/@core/dynamic-tab-page-service';
import { API_ENDPOINTS } from '@app/shared/constants/apiconstants';
import { SupportingDocumentRuleConfigService } from '@app/pages/page-designer/supporting-document-rule-config/supporting-document-rule-config.service';


@Injectable()
export class AuthService {
  sharedInfo: any;
  alertService: AlertService;
  dynamicTabPageService: DynamicTabPageService;
  pageHeaderService: PageHeaderService;
  supportingDocumentRuleConfigService: SupportingDocumentRuleConfigService;
  constructor(
    injector: Injector,
    private httpService: HttpService,
    private store: AuthStore,
    private _router: Router,
    private credentialsService: CredentialsService,
    private localstore: LocalService
  ) {
    this.alertService = injector.get<AlertService>(AlertService);
    this.dynamicTabPageService = injector.get<DynamicTabPageService>(DynamicTabPageService);
    this.pageHeaderService = injector.get<PageHeaderService>(PageHeaderService);
    this.supportingDocumentRuleConfigService = injector.get<SupportingDocumentRuleConfigService>(SupportingDocumentRuleConfigService);
  }

  public orgInfo = new BehaviorSubject<any>('');
  currentOrgInfo = this.orgInfo.asObservable();

  public currentMenu = new BehaviorSubject<any>('');
  currentMenuInfo = this.currentMenu.asObservable();

  feedOrgInfo(data: any) {
    this.orgInfo.next(data);
  }
  getCurrentMenu(data: any) {
    this.currentMenu.next(data);
  }

  getUserOrgList() {
    return this.httpService.get(AuthURL.EndPoints.auth.user.orgList);
  }

  getUnNotified() {
    return this.httpService.get(AuthURL.EndPoints.auth.user.notification);
  }

  updateUnNotified(data) {
    return this.httpService.post(AuthURL.EndPoints.auth.user.notification, data);
  }

  updateWorkerAvailability(data) {
    return this.httpService.patch(AuthURL.EndPoints.auth.user.workerAvailability, data);
  }

  getWorkerAvailability() {
    return this.httpService.get(AuthURL.EndPoints.auth.user.getWorkerAvailability);
  }

  getMstrToken() {
    return this.httpService.get(AuthURL.EndPoints.auth.microstrategy.login).pipe(res => res);
  }

  public login(email: string, password: string, otp: string): Observable<any> {
    const body = {
      email: email,
      password: password,
      secret: otp ? otp : ''
    };
    return this.httpService.post(AuthURL.EndPoints.auth.user.login, body).pipe(
      mergeMap((res: any): Observable<[any]> => {
        if (res['data'] === 'MFA_CODE_SEND') {
          return of(res['data']);
        }
        this.credentialsService.setCredentials(res['data'].idToken.jwtToken);
        this.localstore.setItem('refreshToken', res['data'].refreshToken.token);
        this.localstore.setItem('email', res['data'].idToken.payload['email']);
        this.localstore.setItem('id', res['data'].idToken.payload['custom:id']);
        this.localstore.setItem('username', res['data'].idToken.payload['name']);
       // this.preloadSupportingDocumentLookups();
       // sessionStorage.setItem('username', res['data'].idToken.payload['name']);
           //    sessionStorage.setItem('username', res['data'].idToken.payload['name']);
        return this.getUserInfo();
      })
    );
  }

  public refreshToken(platform = 'aws') {
    const email = this.localstore.getItem('email');
    const refreshToken = this.localstore.getItem('refreshToken');
    const body = {
      email,
      refreshToken
    };
    if (platform === 'aws') {
      return this.httpService.post(AuthURL.EndPoints.auth.user.refreshToken, body).pipe(
        mergeMap((res: any): any => {
          this.credentialsService.setCredentials(res['data'].idToken.jwtToken);
          this.localstore.setItem('refreshToken', res['data'].refreshToken.token);
          this.localstore.setItem('email', res['data'].idToken.payload['email']);
          this.localstore.setItem('id', res['data'].idToken.payload['custom:id']);
          this.localstore.setItem('username', res['data'].idToken.payload['name']);
          sessionStorage.setItem('username', res['data'].idToken.payload['name']);
          console.log('new token generated...', res['data'].idToken.jwtToken);
          return [res['data'].idToken.jwtToken];
        })
      );
    } else {
      return this.httpService.post(AuthURL.EndPoints.auth.user.refreshToken, body).pipe(
        mergeMap((res: any): any => {
          this.credentialsService.setCredentials(res['data'].token);
          this.localstore.setItem('refreshToken', res['data'].refreshToken);
          return [res['data'].token];
        })
      );
    }
  }

  public resetLoggedIn() {
    return this.httpService.post(AuthURL.EndPoints.auth.user.logout, {
      email: this.localstore.getItem('email')
    });
  }

  public logout() {
    this.supportingDocumentRuleConfigService.clearSupportingDocumentLookupCache();
    this._router.navigate(['/login']);
    this.localstore.clear();
    localStorage.clear();
  }

  public getUserInfo(): Observable<[any]> {
    return forkJoin([this.httpService.get(AuthURL.EndPoints.auth.user.userInfo)]).pipe(
      tap(([user]) => {
        this.store.addAuthInfo(user['data']);
        return user;
      })
    );
  }

  public getUserRole(id): Observable<any> {
    return this.httpService.get(AuthURL.EndPoints.auth.user.userRole.replace('{id}', id)).pipe(res => res);
  }

  public routeToDynamicPage(orgid): Observable<any> {
    return this.httpService
      .get(AuthURL.EndPoints.auth.user.routeToDynamicPage.replace('{orgid}', orgid))
      .pipe(map((res: any) => res));
  }

  getAuthMe() {
    return this.httpService.get(AuthURL.EndPoints.auth.user.authMe);
  }

  ResetPassword(data: any) {
    return this.httpService.post(AuthURL.EndPoints.auth.user.resetPassword, data);
  }

  public getRoleKey() {
    const user = this.localstore.getObj('user');
    if (user && user.role) {
      return user.role.rolekey;
    }
    // Explicitly return null if the conditions are not met
    return null;
  }

  public isAdmin() {
    return 'ADM' === this.getRoleKey();
  }

  public getOrgID() {
    const user = this.localstore.getObj('user');
    if (user && user.userWorkInfo && user.userWorkInfo.organization && user.userWorkInfo.organization.id) {
      return user.userWorkInfo.organization.id;
    } else {
      return '';
    }
  }

  conformMail(data) {
    return this.httpService.post(AuthURL.EndPoints.auth.user.conformMail, data);
  }

  changePassword(data) {
    return this.httpService.post(AuthURL.EndPoints.auth.user.changePassword, data);
  }

  setSharedMessage(data) {
    this.sharedInfo = data;
  }

  getSharedMessage() {
    return this.sharedInfo;
  }

  async checkDynamicPagePermission(pageId: any) {
    const dynamicPages = await this.getAuthorizedPages();
    if (pageId) {
      this.dynamicTabPageService.getPageById(pageId).subscribe(res => {
        if (dynamicPages.some(page => page.id === res['data'][0].activeVersion.pageid)) {
          this._router.navigate([`pages/dynamic-search/search/${res['data'][0].activeVersion.id}`]);
        } else if (dynamicPages.some(page => page.id === res['data'][0].activeVersion.id && page.type === 'BGPNEW')) {
          this._router.navigate([`pages/dynamic-grid/search/${res['data'][0].activeVersion.id}`]);
        } else {
          this.alertService.error(
            `You don't have permissions for ${res['data'][0].activeVersion.pagename} . Please Contact Administrator`
          );
        }
      });
    } else {
      this.alertService.error(
        "You don't have permissions to perform the following operations .Please Contact Administrator"
      );
    }
  }

  getCurrentOrg() {
    return this.getUserOrgList()
      .toPromise()
      .then(response => response['data'][0].id);
  }

  async getAuthorizedPages() {
    const orgId = await this.getCurrentOrg();
    return this.pageHeaderService
      .getAuthorizedPages(orgId)
      .toPromise()
      .then(
        response => {
          const dynamicPage = response['data']['data'].filter(
            page =>
              page.activeVersion &&
              (page.gridconfig || page.templatejson)
          );
          return dynamicPage.map(page => ({
            id: page.id,
            name: page.pagename,
            activeVersion: page.activeVersion
          }));
        },
        _error => this.alertService.error(AppConstants.errorMessage)
      );
  }

  getCustomPage(page) {
    if (page.activeVersion.tabconfig) {
      const tabconfig =
        typeof page.activeVersion.tabconfig === 'string'
          ? JSON.parse(page.activeVersion.tabconfig)
          : page.activeVersion.tabconfig;
      const routingTab = tabconfig.filter(x => x.type === 'ROUTING');
      return routingTab.length ? page : null;
    }
    // Return null if page.activeVersion.tabconfig is falsy
    return null;
  }

  public validateToken(token: string, requestObject: any): Observable<any> {
    this.credentialsService.setCredentials(token);
    return this.httpService.getAuthAccessKey(AuthURL.EndPoints.auth.user.getTokenValidationUrl, requestObject).pipe(
      mergeMap((res: any): Observable<[any]> => {
        if (res['data'] === 'MFA_CODE_SEND') {
          return of(res['data']);
        }
        this.credentialsService.setCredentials(res['data'].token);
        this.localstore.setItem('refreshToken', res['data'].refreshToken);
        this.localstore.setItem('email', res['data'].email);
        this.localstore.setItem('id', res['data'].id);
        this.localstore.setItem('username', res['data'].name);
        //sessionStorage.setItem('username', res['data'].name);
        sessionStorage.setItem('username', res['data'].name);
       // this.preloadSupportingDocumentLookups();
       this.logUserLastLogin(res['data'].id).subscribe();
        return this.getUserInfo();
      })
    );
  }
logUserLastLogin(userId: number) {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  sessionStorage.setItem('timeZone', timeZone);
  this.localstore.setItem('timeZone', timeZone);
  console.log("user time zone:",timeZone )
  return this.httpService.post(API_ENDPOINTS.lastLogin, { user_id: userId });
}

  resetPassword(data: UnknownRecord): Observable<UnknownRecord> {
    return this.httpService.post<UnknownRecord, UnknownRecord>(AuthURL.EndPoints.auth.user.resetPassword, data);
  }

  generateLoginUrl() {
    return this.httpService.get(AuthURL.EndPoints.auth.user.generateLoginUrl);
  }

  isTokenExpired() {
    const helper = new JwtHelperService();
    return this.getToken() && helper.isTokenExpired(this.getToken());
  }

  getTokenDetails() {
    const helper = new JwtHelperService();
    return this.getToken() && helper.decodeToken(this.getToken());
  }

  getToken(): any {
    return this.localstore.getItem('jwt-token');
  }
  setCookie(data: UnknownRecord): Observable<unknown> { return this.httpService.post(AuthURL.EndPoints.auth.user.setCookie, data); }

  public preloadSupportingDocumentLookups(): void {
    setTimeout(() => {
        forkJoin([
      this.supportingDocumentRuleConfigService.getSupportingDocumentCategoriesWithTypes(),
      this.supportingDocumentRuleConfigService.getSupportingDocumentStatuses()
    ]).subscribe({
      error: (error: any) => {
        console.warn('Unable to preload supporting document lookup cache.', error);
      }
    });
    }, 5000);
    
  }
}

 type UnknownRecord = Record<string, unknown>;
